/**
 * Module keys — frontend mirror of the backend MODULE_KEYS.
 *
 * These constants match `Module.code` values in the database.
 * Use them instead of raw strings for type safety across the frontend.
 *
 * @see crm-backend/src/modules/registry.ts for the authoritative source.
 */
export const MODULE_KEYS = {
  CUSTOMERS: 'customers',
  CUSTOMER_DETAILS: 'customer_details',
  CONTRACTS: 'contracts',
  ADMINISTRATIVE: 'administrative',
  GROUPS_AND_PRODUCTS: 'groups_and_products',
  TRANSACTIONS: 'transactions',
  STOCK: 'stock',
  RATE_PLANS: 'rate_plans',
  TASKS: 'tasks',
  USERS: 'users',
  COMMISSIONS: 'commissions',
  LEADS: 'leads',
  BANK_VERIFICATION: 'bank_verification',
  AGENT_AGREEMENTS: 'agent_agreements',
  NOTES: 'notes',
  CALLS: 'calls',
  REPORTS: 'reports',
  WORKSHOP: 'workshop',
  BANK_STATEMENTS: 'bank_statements',
  RECONCILIATION: 'reconciliation',
  HR: 'hr',
  HR_GRIEVANCES: 'hr_grievances',
  HR_WORKFORCE: 'hr_workforce',
  HR_DEMOGRAPHICS: 'hr_demographics',
  HR_PERFORMANCE: 'hr_performance',
  HR_CASES: 'hr_cases',
  HR_TRAINING: 'hr_training',
  HR_RECRUITMENT: 'hr_recruitment',
  HR_SAFETY: 'hr_safety',
  HR_SUBCONTRACTORS: 'hr_subcontractors',
  HR_ESG: 'hr_esg',
  HR_ATTENDANCE: 'hr_attendance',
  HR_PAYROLL: 'hr_payroll',
  SMS_CAMPAIGNS: 'sms_campaigns',
  SALES_APP_MESSAGES: 'sales_app_messages',
} as const;

export type ModuleKey = (typeof MODULE_KEYS)[keyof typeof MODULE_KEYS];

/**
 * The children of HR, one per sensitive area (ADR-0061 §4 in the backend).
 * Holding `view` on any of them opens HR and its dashboard; the dashboard then
 * draws a tile only for the areas the person can view.
 */
export const HR_CHILD_MODULES: readonly string[] = [
  MODULE_KEYS.HR_WORKFORCE,
  MODULE_KEYS.HR_DEMOGRAPHICS,
  MODULE_KEYS.HR_PERFORMANCE,
  MODULE_KEYS.HR_CASES,
  MODULE_KEYS.HR_TRAINING,
  MODULE_KEYS.HR_RECRUITMENT,
  MODULE_KEYS.HR_SAFETY,
  MODULE_KEYS.HR_SUBCONTRACTORS,
  MODULE_KEYS.HR_ESG,
  MODULE_KEYS.HR_ATTENDANCE,
  MODULE_KEYS.HR_PAYROLL,
  MODULE_KEYS.HR_GRIEVANCES,
];

/**
 * Modules a SuperAdmin does not get into automatically (ADR-0060 and ADR-0061
 * in the backend). Every HR child follows the grievance rule: reading one takes
 * an explicit grant; the backend refuses the rest, and the UI must not draw
 * what it refuses.
 */
export const NO_ADMIN_BYPASS_MODULES: ReadonlySet<string> = new Set(HR_CHILD_MODULES);

/**
 * The grant that opens each report, on top of `reports:view` — frontend mirror
 * of the backend REPORT_ACTIONS. Keyed by the report's id in the report library.
 */
export const REPORT_ACTIONS = {
  receivables: 'view_receivables',
  badDebtors: 'view_bad_debtors',
  edf: 'view_edf',
  financeWorkbooks: 'view_finance_workbooks',
  customerBook: 'view_customer_book',
  customerDemographics: 'view_customer_demographics',
  agentHierarchy: 'view_agent_hierarchy',
  stockSales: 'view_stock_sales',
  stockByTier: 'view_stock_by_tier',
  stock: 'view_stock_reports',
  warranty: 'view_warranty_claims',
  // Off by default: the backend seeds and backfills every report grant but these two
  // (stock valuation starts with Administrator alone).
  digitalChannels: 'view_digital_channels',
  stockValuation: 'view_stock_valuation',
} as const;

export type ReportId = keyof typeof REPORT_ACTIONS;
